import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileCompletionPrompt } from '@/components/ProfileCompletionPrompt';
import { SocialMediaLinksTab } from '@/components/profile/SocialMediaLinksTab';
import {
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Trophy,
  Phone,
  Mail,
  Globe,
  Briefcase,
  Star,
  TrendingUp,
  Award,
  UserCheck,
  Target,
  Percent,
  Edit,
  User,
  Settings
} from 'lucide-react';
import { useAgentData } from '@/hooks/useAgentData';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import AgentProfileEditDrawer from './AgentProfileEditDrawer';
import { userService } from '@/services/userService';
import { AthletesTab } from './AthletesTab';
import { DealProgressTab } from '@/components/deals/DealProgressTab';
import { AdminProfileSection } from '@/components/admin/AdminProfileSection';

const AgentProfile = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useUser();
  const { isAdmin } = useUserRole();
  const { agentData, loading, error } = useAgentData();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editSection, setEditSection] = useState<'overview' | 'professional' | 'contact'>('overview');

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 bg-muted animate-pulse rounded" />
                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error || !agentData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-muted-foreground">
              {error || 'Unable to load agent profile. Please try again.'}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!agentData.agent) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Agent profile data not found. Please complete your agent profile setup.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const agent = agentData.agent;
  const profile = agentData;

  const handleEditClick = (section: 'overview' | 'professional' | 'contact') => {
    setEditSection(section);
    setEditDrawerOpen(true);
  };

  const handleSave = async (updatedData: any) => {
    try {
      await userService.updateProfile(profile.id, updatedData);
      await refreshUserData();
    } catch (error) {
      throw error;
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  return (
    <div className="space-y-6">
      <ProfileCompletionPrompt />
      
      {/* Admin Section */}
      {isAdmin && <AdminProfileSection />}
      
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-lg">
                  {profile.display_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button
                variant="outline"
                size="icon"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md bg-background"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {agent.professional_name || profile.display_name}
                  </h1>
                  <p className="text-muted-foreground">
                    Sports Agent at {agent.agency_name || 'Independent'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {profile.is_verified && (
                    <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  <Badge variant="outline">
                    Agent
                  </Badge>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Joined {new Date(profile.created_at).toLocaleDateString()}
                </div>
                {agent.years_experience && (
                  <div className="flex items-center gap-1">
                    <Briefcase className="h-4 w-4" />
                    {agent.years_experience} years experience
                  </div>
                )}
              </div>

              {profile.bio && (
                <p className="mt-4 text-foreground leading-relaxed">
                  {profile.bio}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {agent.client_roster?.length || 0}
            </div>
            <div className="text-sm text-muted-foreground">Active Clients</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Trophy className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {agent.total_deals || 0}
            </div>
            <div className="text-sm text-muted-foreground">Total Deals</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {agent.success_rate || 0}%
            </div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Percent className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-foreground">
              {agent.commission_rate || 0}%
            </div>
            <div className="text-sm text-muted-foreground">Commission Rate</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Profile Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-6">
          <TabsTrigger value="overview" className="whitespace-nowrap text-xs md:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="professional" className="whitespace-nowrap text-xs md:text-sm">Professional</TabsTrigger>
          <TabsTrigger value="deals" className="whitespace-nowrap text-xs md:text-sm">Deal Progress</TabsTrigger>
          <TabsTrigger value="athletes" className="whitespace-nowrap text-xs md:text-sm">Athletes</TabsTrigger>
          <TabsTrigger value="contact" className="whitespace-nowrap text-xs md:text-sm">Contact Info</TabsTrigger>
          <TabsTrigger value="social" className="whitespace-nowrap text-xs md:text-sm">Social Media</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Profile Overview
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('overview')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Display Name</span>
                <span className="font-medium">{profile.display_name || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start">
                <span className="text-sm text-muted-foreground sm:flex-shrink-0">Bio</span>
                <span className="font-medium sm:text-right sm:max-w-xs sm:truncate">{profile.bio || 'Not provided'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="font-medium">{profile.location || 'Not provided'}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="professional" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Professional Details
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('professional')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Professional Name</span>
                <span className="font-medium">{agent.professional_name || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Agency</span>
                <span className="font-medium">{agent.agency_name || 'Independent'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Experience</span>
                <span className="font-medium">{agent.years_experience || 0} years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Success Rate</span>
                <span className="font-medium">{agent.success_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Commission Rate</span>
                <span className="font-medium">{agent.commission_rate || 0}%</span>
              </div>
              {agent.specialization && agent.specialization.length > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground block mb-2">Specializations</span>
                  <div className="flex flex-wrap gap-2">
                    {agent.specialization.map((spec: string, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals" className="space-y-6">
          <DealProgressTab selectedAthleteId="all" />
        </TabsContent>

        <TabsContent value="athletes" className="space-y-4">
          <AthletesTab agentData={agent} />
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('contact')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="font-medium">{profile.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Professional Email</span>
                <span className="font-medium">{agent.professional_email || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span className="font-medium">{agent.phone || 'Not specified'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Account Type</span>
                <Badge variant="outline">{profile.account_type}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Profile Status</span>
                <Badge variant={profile.setup_completed ? "default" : "secondary"}>
                  {profile.setup_completed ? 'Complete' : 'Incomplete'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <SocialMediaLinksTab />
        </TabsContent>
      </Tabs>

      <AgentProfileEditDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        section={editSection}
        data={agentData}
        onSave={handleSave}
      />
    </div>
  );
};

export default AgentProfile;